import { supabase } from './supabase';

// Local (IST) date YYYY-MM-DD — toISOString() gives the UTC date, which is "yesterday" before 5:30am IST.
const localDate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Send a notification to all staff, or to one staff member
 * @param {Object} options
 * @param {string} options.title - Notification title
 * @param {string} options.message - Optional message body
 * @param {string} options.type - 'info' | 'warning' | 'error' | 'success' | 'reminder'
 * @param {string} options.link - Optional route to navigate to e.g. '/fees'
 * @param {string} options.createdBy - Staff ID who created it
 * @param {string} options.studentId - Optional student ID
 * @param {string} options.staffId - Optional: send only to this staff member (otherwise it is global)
 * @param {boolean} options.dedupe - If true, skip when an identical notification was already sent today
 */
export async function sendNotification({ title, message = '', type = 'info', link = null, createdBy = null, studentId = null, staffId = null, dedupe = false }) {
  try {
    if (dedupe) {
      const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
      const { data: existing } = await supabase.from('notifications')
        .select('id').eq('title', title).eq('message', message)
        .gte('created_at', startOfDay.toISOString()).limit(1);
      if (existing && existing.length) return true; // already sent today
    }
    const row = {
      title,
      message,
      type,
      link,
      is_global: !staffId,
      created_by: createdBy,
      student_id: studentId || null,
    };
    if (staffId) row.staff_id = staffId;
    const { error } = await supabase.from('notifications').insert(row);
    if (error) { console.error('Notification error:', error); return false; }
    return true;
  } catch (e) {
    console.error('Notification error:', e);
    return false;
  }
}

/**
 * Auto-generate reminders for upcoming events
 * Call this on login or periodically — safe to call repeatedly (each reminder is sent at most once per day).
 */
export async function generateAutoReminders(staffId) {
  try {
    const today = localDate();
    const in7Days = localDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
    const sevenDaysAgo = localDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

    // Hostel vacate dates approaching (next 7 days) for students who haven't vacated yet
    const { data: hostelVacating } = await supabase
      .from('hostel_allocations')
      .select('*, students(full_name, register_number)')
      .gte('hostel_vacate_date', today)
      .lte('hostel_vacate_date', in7Days)
      .is('vacate_date', null)
      .limit(200);

    for (const h of (hostelVacating || [])) {
      await sendNotification({
        title: `🏠 Hostel Vacate Approaching`,
        message: `${h.students?.full_name || 'Student'} (${h.students?.register_number || '—'}) is due to vacate on ${h.hostel_vacate_date}`,
        type: 'warning',
        link: '/hostel',
        createdBy: staffId,
        dedupe: true,
      });
    }

    // Certificate requests still "submitted" for more than 7 days
    const { count } = await supabase
      .from('certificate_requests')
      .select('id', { count: 'exact', head: true })
      .eq('processing_status', 'submitted')
      .lt('request_date', sevenDaysAgo);

    if (count && count > 0) {
      await sendNotification({
        title: `📋 ${count} Certificate Request${count > 1 ? 's' : ''} Pending`,
        message: `${count} certificate request${count > 1 ? 's are' : ' is'} waiting for more than 7 days`,
        type: 'reminder',
        link: '/certificates',
        createdBy: staffId,
        dedupe: true,
      });
    }
    // Fee installments: due in the next 3 days, and overdue by more than 7 days (one summary per day, not per student)
    const in3Days = localDate(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000));
    const { count: dueSoon } = await supabase
      .from('student_fee_schedules')
      .select('id', { count: 'exact', head: true })
      .in('status', ['pending', 'partial'])
      .gte('due_date', today)
      .lte('due_date', in3Days);
    if (dueSoon && dueSoon > 0) {
      await sendNotification({
        title: `💰 ${dueSoon} fee installment${dueSoon > 1 ? 's' : ''} due in the next 3 days`,
        message: 'Open Fee Schedules → Reports → Reminder List to follow up',
        type: 'reminder',
        createdBy: staffId,
        dedupe: true,
      });
    }
    const { count: overdueLong } = await supabase
      .from('student_fee_schedules')
      .select('id', { count: 'exact', head: true })
      .in('status', ['pending', 'partial'])
      .lt('due_date', sevenDaysAgo);
    if (overdueLong && overdueLong > 0) {
      await sendNotification({
        title: `⚠️ ${overdueLong} fee installment${overdueLong > 1 ? 's' : ''} overdue by more than 7 days`,
        message: 'Open Fee Schedules → Reports → Defaulter List (choose "All ongoing batches")',
        type: 'warning',
        createdBy: staffId,
        dedupe: true,
      });
    }
    // Assignments: overdue (past due date, still pending) — one summary per day; skipped quietly if due_date column is missing
    try {
      const { count: asgOverdue, error: asgErr } = await supabase
        .from('assignments')
        .select('id', { count: 'exact', head: true })
        .eq('corrected', 'pending')
        .not('due_date', 'is', null)
        .lt('due_date', today);
      if (!asgErr && asgOverdue && asgOverdue > 0) {
        await sendNotification({
          title: `📝 ${asgOverdue} assignment${asgOverdue > 1 ? 's are' : ' is'} overdue`,
          message: 'Open Assignments → click the Overdue card to follow up',
          type: 'warning',
          link: '/assignments',
          createdBy: staffId,
          dedupe: true,
        });
      }
    } catch (e) { /* optional */ }
  } catch (e) {
    console.error('Auto reminder error:', e);
  }
}
